import React, { createContext, useCallback, useRef, useState } from "react";
import pinService, { EMPTY_PINS } from "../../services/pinService";
import { useToast } from "./ToastContext";

export const PinsContext = createContext(null);

export const PinsProvider = ({ children }) => {
  const [pins, setPins] = useState(EMPTY_PINS);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | unavailable
  const loadStarted = useRef(false);
  const statusRef = useRef("idle");
  const pinsRef = useRef(EMPTY_PINS); // latest optimistic list
  const savedRef = useRef(EMPTY_PINS); // last list the server confirmed — the rollback target
  const queue = useRef(Promise.resolve());
  const { showToast } = useToast();

  const applyPins = useCallback((next) => {
    pinsRef.current = next;
    setPins(next);
  }, []);

  const applyStatus = useCallback((next) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  // Deferred until a consumer mounts, because PinsProvider also wraps the unauthenticated routes.
  const ensureLoaded = useCallback(async () => {
    if (loadStarted.current) return;
    loadStarted.current = true;
    applyStatus("loading");
    try {
      const userName = await pinService.getUserName();
      applyPins(pinService.readCache(userName)); // paints first; never authoritative
      const server = await pinService.fetchAll();
      savedRef.current = server;
      applyPins(server);
      applyStatus("ready");
    } catch (error) {
      console.error("Unable to load pins:", error);
      applyStatus("unavailable");
    }
  }, [applyPins, applyStatus]);

  // One save in flight at a time. Each toggle enqueues its own (namespace, id, action)
  // intent rather than re-sending the latest full list, so this just runs whatever
  // action is next in line and applies the set the server hands back.
  const enqueue = useCallback(
    (action) => {
      queue.current = queue.current
        .then(async () => {
          const saved = await action();
          savedRef.current = saved;
          applyPins(saved);
        })
        .catch((error) => {
          console.error("Unable to save pin:", error);
          applyPins(savedRef.current);
          showToast("Couldn't save your pin", "error");
        });
      return queue.current;
    },
    [applyPins, showToast],
  );

  const toggle = useCallback(
    (namespace, id) => {
      if (!id || statusRef.current !== "ready") return Promise.resolve();
      const list = pinsRef.current[namespace] || [];
      const alreadyPinned = list.includes(id);
      applyPins({
        ...pinsRef.current,
        [namespace]: alreadyPinned
          ? list.filter((x) => x !== id)
          : [id, ...list],
      });
      return enqueue(() =>
        alreadyPinned
          ? pinService.unpin(namespace, id)
          : pinService.pin(namespace, id),
      );
    },
    [applyPins, enqueue],
  );

  return (
    <PinsContext.Provider value={{ pins, status, toggle, ensureLoaded }}>
      {children}
    </PinsContext.Provider>
  );
};

export default PinsProvider;
